/**
 * 檔案用途區塊
 * @module ThirdPartyProviderGuard
 * @purpose 集中檢查第三方登入必要參數是否齊備，僅在可用狀態下渲染受保護內容。
 * @external https://innotech.atlassian.net/browse/IN-46601
 * @external https://innotech.atlassian.net/browse/IN-47655
 */
import React from 'react';
import { IBaseComponentProp } from '@shared/interfaces/base-component.interface';
import { useThirdPartyProvider } from '@shared/models';
import type { ThirdPartyPayload } from '@shared/models';
import { getProviderContract } from '@shared/third-party-providers/contracts';
import Show from '@shared/components/Show';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 provider contract 判斷 payload 是否可進入 SSO 流程。
 * @purpose Passkey 與 OAuth 使用不同必填規則，避免 Guard 硬編碼 clientId。
 * @external https://innotech.atlassian.net/browse/IN-132161
 */
const isPayloadReady = (payload: ThirdPartyPayload): boolean => {
  const contract = getProviderContract(payload.provider ?? undefined);
  if (!contract) {
    return false;
  }

  return contract.validate(payload).ready;
};

/**
 * 宣告內容用途說明與單號關聯
 * @description ThirdPartyProviderGuard 元件。
 * @purpose 以第三方登入設定完整性作為門檻，決定是否顯示 children。
 * @external https://innotech.atlassian.net/browse/IN-46601
 */
const ThirdPartyProviderGuard = ({ children }: IBaseComponentProp) => {
  /**
   * 宣告內容用途說明與單號關聯
   * @description 讀取第三方登入流程所需的完整 payload。
   * @purpose 取得當前登入供應商設定，供後續 contract 驗證使用。
   * @external https://innotech.atlassian.net/browse/IN-47655
   */
  const { payload } = useThirdPartyProvider();
  /**
   * 宣告內容用途說明與單號關聯
   * @description 將 payload 轉為布林值後判斷是否可啟用第三方登入流程。
   * @purpose 避免設定缺漏時渲染後續登入相關內容。
   * @external https://innotech.atlassian.net/browse/IN-47655
   */
  const isReady = isPayloadReady(payload);

  return <Show when={isReady}>{children}</Show>;
};

export default ThirdPartyProviderGuard;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:23:51.313Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout (top/middle/bottom), inserted declaration-level JSDoc blocks mapped to provided declaration origins, formatted all @external tags as full Jira URLs with deduped tickets, and kept runtime logic unchanged.
 */
